<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <b-form>
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("dmp.timeSchedules.title") }}</h5>
              <div class="d-flex gap-1">
                <b-button
                  variant="primary"
                  size="sm"
                  @click="addNewSchedule"
                  :disabled="isLoadingSchedules"
                  class="text-capitalize"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  {{ $t("dmp.timeSchedules.addNew") }}
                </b-button>
              </div>
            </div>

            <div v-if="isLoadingSchedules" class="text-center py-4">
              <b-spinner
                variant="primary"
                :label="$t('dmp.timeSchedules.loading')"
              ></b-spinner>
              <p class="mt-2 mb-0">{{ $t("dmp.timeSchedules.loading") }}...</p>
            </div>

            <b-row v-if="!isLoadingSchedules">
              <b-col cols="4" class="table-column">
                <!-- Time Schedules Table -->
                <b-table
                  :items="localSchedules"
                  :fields="scheduleTableFields"
                  responsive
                  striped
                  hover
                  show-empty
                  :empty-text="$t('dmp.timeSchedules.noSchedulesConfigured')"
                  selectable
                  select-mode="single"
                  @row-selected="onRowSelected"
                  sort-by="number"
                  :sort-desc="false"
                  class="mb-0"
                  :tbody-tr-class="rowClass"
                >
                  <template #cell(number)="data">
                    {{ parseInt(data.item.scheduleNumber) }}
                  </template>

                  <template #cell(name)="data">
                    {{ data.item.scheduleName || "-" }}
                  </template>
                </b-table>
              </b-col>

              <b-col cols="8" class="details-column">
                <!-- Selected Schedule Details Card -->
                <b-card v-if="selectedSchedule" no-body>
                  <b-card-header class="mb-1 pt-0">
                    <h6 class="mb-0">{{ $t("dmp.timeSchedules.details") }}</h6>
                  </b-card-header>
                  <b-card-body>
                    <validation-observer ref="scheduleFormObserver">
                      <b-form @submit.prevent="saveSchedule">
                        <b-row>
                          <!-- Schedule Number and Name -->
                          <b-col cols="12" md="6">
                            <validation-provider
                              #default="validationContext"
                              :name="$t('dmp.timeSchedules.scheduleNumber')"
                              rules="required|integer|min_value:1|max_value:99"
                            >
                              <b-form-group
                                :label="$t('dmp.timeSchedules.scheduleNumber')"
                                label-for="schedule-number"
                              >
                                <b-form-input
                                  id="schedule-number"
                                  v-model="selectedSchedule.scheduleNumber"
                                  :state="getValidationState(validationContext)"
                                  maxlength="2"
                                  :disabled="isEditingSchedule"
                                />
                                <b-form-text class="text-muted">
                                  <small>{{
                                    $t("dmp.timeSchedules.scheduleNumberRange")
                                  }}</small>
                                </b-form-text>
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <b-col cols="12" md="6">
                            <validation-provider
                              #default="validationContext"
                              :name="$t('dmp.timeSchedules.scheduleName')"
                              rules="required|max:32"
                            >
                              <b-form-group
                                :label="$t('dmp.timeSchedules.scheduleName')"
                                label-for="schedule-name"
                              >
                                <b-form-input
                                  id="schedule-name"
                                  v-model="selectedSchedule.scheduleName"
                                  :state="getValidationState(validationContext)"
                                  maxlength="32"
                                  @input="
                                    (val) =>
                                      (selectedSchedule.scheduleName = val.toUpperCase())
                                  "
                                />
                                <b-form-invalid-feedback>
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </b-col>

                          <!-- Temporary Schedule Section -->
                          <b-col cols="12">
                            <b-row>
                              <b-col cols="12">
                                <b-form-checkbox
                                  v-model="temporaryScheduleEnabled"
                                  switch
                                  class="mb-2"
                                >
                                  <strong>{{
                                    $t("dmp.timeSchedules.temporarySchedule")
                                  }}</strong>
                                </b-form-checkbox>
                              </b-col>
                              <b-col cols="12" md="6" v-if="temporaryScheduleEnabled">
                                <b-form-group
                                  :label="$t('dmp.timeSchedules.beginDate')"
                                  label-for="begin-date"
                                >
                                  <flat-pickr
                                    id="begin-date"
                                    v-model="selectedSchedule.beginDate"
                                    :config="datePickerConfig"
                                    class="form-control"
                                    placeholder="DD/MM"
                                  />
                                  <b-form-text class="text-muted">
                                    <small>Format: DD/MM</small>
                                  </b-form-text>
                                </b-form-group>
                              </b-col>
                              <b-col cols="12" md="6" v-if="temporaryScheduleEnabled">
                                <b-form-group
                                  :label="$t('dmp.timeSchedules.endDate')"
                                  label-for="end-date"
                                >
                                  <flat-pickr
                                    id="end-date"
                                    v-model="selectedSchedule.endDate"
                                    :config="datePickerConfig"
                                    class="form-control"
                                    placeholder="DD/MM"
                                  />
                                  <b-form-text class="text-muted">
                                    <small>Format: DD/MM</small>
                                  </b-form-text>
                                </b-form-group>
                              </b-col>
                            </b-row>
                          </b-col>

                          <!-- Schedule Times Section -->
                          <b-col cols="12">
                            <b-row>
                              <!-- Open/Begin/Activate Column -->
                              <b-col cols="6">
                                <div class="d-flex align-items-center mb-2">
                                  <h6 class="mb-0">
                                    {{ $t("dmp.timeSchedules.openBeginActivate") }}
                                  </h6>
                                  <b-button
                                    v-if="copiedTime.open"
                                    variant="flat-primary"
                                    size="sm"
                                    class="ml-auto btn-icon"
                                    v-b-tooltip.hover
                                    :title="$t('dmp.timeSchedules.applyToAll')"
                                    @click="applyTimeToAll('open')"
                                  >
                                    <feather-icon icon="ChevronsDownIcon" size="14" />
                                  </b-button>
                                </div>

                                <div
                                  v-for="day in weekDays"
                                  :key="`${day}-open`"
                                  class="mb-2"
                                >
                                  <b-form-group
                                    :label="$t(`dmp.timeSchedules.days.${day}`)"
                                    :label-for="`${day}-open`"
                                    label-cols="3"
                                    label-size="sm"
                                    class="mb-0"
                                  >
                                    <div v-if="isDaySelectedForSunrise(day, 'open')">
                                      <b-form-input
                                        size="sm"
                                        disabled
                                        :value="getSunriseString('open')"
                                      />
                                    </div>
                                    <div v-else class="d-flex align-items-center">
                                      <b-form-timepicker
                                        :id="`${day}-open`"
                                        v-model="selectedSchedule[`${day}OpenTime`]"
                                        size="sm"
                                        :locale="currentLocale"
                                        :hour12="true"
                                        hide-seconds
                                        hide-header
                                        reset-button
                                        :label-reset-button="$t('dmp.timeSchedules.resetButton')"
                                        :label-close-button="$t('dmp.timeSchedules.closeButton')"
                                        :placeholder="$t('dmp.timeSchedules.noTimeSelected')"
                                        class="flex-grow-1"
                                      />
                                      <b-button
                                        v-if="selectedSchedule[`${day}OpenTime`]"
                                        variant="flat-secondary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.copyTime')"
                                        @click="copyTime('open', selectedSchedule[`${day}OpenTime`])"
                                      >
                                        <feather-icon icon="CopyIcon" size="12" />
                                      </b-button>
                                      <b-button
                                        v-if="copiedTime.open && selectedSchedule[`${day}OpenTime`] !== copiedTime.open"
                                        variant="flat-primary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.pasteTime')"
                                        @click="pasteTime('open', `${day}OpenTime`)"
                                      >
                                        <feather-icon icon="ClipboardIcon" size="12" />
                                      </b-button>
                                    </div>
                                  </b-form-group>
                                </div>

                                <!-- Holiday times -->
                                <div
                                  v-for="holiday in holidays"
                                  :key="`${holiday}-open`"
                                  class="mb-2"
                                >
                                  <b-form-group
                                    :label="$t(`dmp.timeSchedules.holidays.${holiday}`)"
                                    :label-for="`${holiday}-open`"
                                    label-cols="3"
                                    label-size="sm"
                                    class="mb-0"
                                  >
                                    <div class="d-flex align-items-center">
                                      <b-form-timepicker
                                        :id="`${holiday}-open`"
                                        v-model="selectedSchedule[`${holiday}OpenTime`]"
                                        size="sm"
                                        :locale="currentLocale"
                                        :hour12="true"
                                        hide-seconds
                                        hide-header
                                        reset-button
                                        :label-reset-button="$t('dmp.timeSchedules.resetButton')"
                                        :label-close-button="$t('dmp.timeSchedules.closeButton')"
                                        :placeholder="$t('dmp.timeSchedules.noTimeSelected')"
                                        class="flex-grow-1"
                                      />
                                      <b-button
                                        v-if="selectedSchedule[`${holiday}OpenTime`]"
                                        variant="flat-secondary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.copyTime')"
                                        @click="copyTime('open', selectedSchedule[`${holiday}OpenTime`])"
                                      >
                                        <feather-icon icon="CopyIcon" size="12" />
                                      </b-button>
                                      <b-button
                                        v-if="copiedTime.open && selectedSchedule[`${holiday}OpenTime`] !== copiedTime.open"
                                        variant="flat-primary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.pasteTime')"
                                        @click="pasteTime('open', `${holiday}OpenTime`)"
                                      >
                                        <feather-icon icon="ClipboardIcon" size="12" />
                                      </b-button>
                                    </div>
                                  </b-form-group>
                                </div>

                                <!-- Sunrise/Sunset Options -->
                                <div class="mt-3 mb-2">
                                  <validation-provider
                                    :name="$t('dmp.timeSchedules.sunriseSunset')"
                                    :rules="sunriseDays.open.length > 0 ? 'required' : ''"
                                    v-slot="{ errors, valid }"
                                  >
                                    <b-form-group
                                      :label="$t('dmp.timeSchedules.sunriseSunset')"
                                      label-for="sunrise-sunset-open"
                                      label-cols="3"
                                      label-size="sm"
                                      :state="errors[0] ? false : valid ? true : null"
                                      :invalid-feedback="errors[0]"
                                    >
                                      <b-form-select
                                        id="sunrise-sunset-open"
                                        v-model="sunriseSelected.open"
                                        :options="sunriseOptionsList"
                                        size="sm"
                                        :state="errors[0] ? false : valid ? true : null"
                                      />
                                    </b-form-group>
                                  </validation-provider>
                                </div>

                                <div class="mb-2">
                                  <validation-provider
                                    :name="$t('dmp.timeSchedules.beforeAfter')"
                                    :rules="sunriseDays.open.length > 0 ? 'required' : ''"
                                    v-slot="{ errors, valid }"
                                  >
                                    <b-form-group
                                      :label="$t('dmp.timeSchedules.beforeAfter')"
                                      label-for="before-after-open"
                                      label-cols="3"
                                      label-size="sm"
                                      :state="errors[0] ? false : valid ? true : null"
                                      :invalid-feedback="errors[0]"
                                    >
                                      <b-form-select
                                        id="before-after-open"
                                        v-model="beforeAfterSelected.open"
                                        :options="beforeAfterOptionsList"
                                        size="sm"
                                        :state="errors[0] ? false : valid ? true : null"
                                      />
                                    </b-form-group>
                                  </validation-provider>
                                </div>

                                <!-- Days checkboxes -->
                                <div class="pl-3">
                                  <b-form-checkbox-group
                                    v-model="sunriseDays.open"
                                    :options="daysCheckboxOptions"
                                    size="sm"
                                    class="days-checkbox-group"
                                  />
                                </div>
                              </b-col>

                              <!-- Close/End Column -->
                              <b-col cols="6">
                                <div class="d-flex align-items-center mb-2">
                                  <h6 class="mb-0">
                                    {{ $t("dmp.timeSchedules.closeEnd") }}
                                  </h6>
                                  <b-button
                                    v-if="copiedTime.close"
                                    variant="flat-primary"
                                    size="sm"
                                    class="ml-auto btn-icon"
                                    v-b-tooltip.hover
                                    :title="$t('dmp.timeSchedules.applyToAll')"
                                    @click="applyTimeToAll('close')"
                                  >
                                    <feather-icon icon="ChevronsDownIcon" size="14" />
                                  </b-button>
                                </div>

                                <div
                                  v-for="day in weekDays"
                                  :key="`${day}-close`"
                                  class="mb-2"
                                >
                                  <b-form-group
                                    :label="$t(`dmp.timeSchedules.days.${day}`)"
                                    :label-for="`${day}-close`"
                                    label-cols="3"
                                    label-size="sm"
                                    class="mb-0"
                                  >
                                    <div v-if="isDaySelectedForSunrise(day, 'close')">
                                      <b-form-input
                                        size="sm"
                                        disabled
                                        :value="getSunriseString('close')"
                                      />
                                    </div>
                                    <div v-else class="d-flex align-items-center">
                                      <b-form-timepicker
                                        :id="`${day}-close`"
                                        v-model="selectedSchedule[`${day}CloseTime`]"
                                        size="sm"
                                        :locale="currentLocale"
                                        :hour12="true"
                                        hide-seconds
                                        hide-header
                                        reset-button
                                        :label-reset-button="$t('dmp.timeSchedules.resetButton')"
                                        :label-close-button="$t('dmp.timeSchedules.closeButton')"
                                        :placeholder="$t('dmp.timeSchedules.noTimeSelected')"
                                        class="flex-grow-1"
                                      />
                                      <b-button
                                        v-if="selectedSchedule[`${day}CloseTime`]"
                                        variant="flat-secondary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.copyTime')"
                                        @click="copyTime('close', selectedSchedule[`${day}CloseTime`])"
                                      >
                                        <feather-icon icon="CopyIcon" size="12" />
                                      </b-button>
                                      <b-button
                                        v-if="copiedTime.close && selectedSchedule[`${day}CloseTime`] !== copiedTime.close"
                                        variant="flat-primary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.pasteTime')"
                                        @click="pasteTime('close', `${day}CloseTime`)"
                                      >
                                        <feather-icon icon="ClipboardIcon" size="12" />
                                      </b-button>
                                    </div>
                                  </b-form-group>
                                </div>

                                <!-- Holiday times -->
                                <div
                                  v-for="holiday in holidays"
                                  :key="`${holiday}-close`"
                                  class="mb-2"
                                >
                                  <b-form-group
                                    :label="$t(`dmp.timeSchedules.holidays.${holiday}`)"
                                    :label-for="`${holiday}-close`"
                                    label-cols="3"
                                    label-size="sm"
                                    class="mb-0"
                                  >
                                    <div class="d-flex align-items-center">
                                      <b-form-timepicker
                                        :id="`${holiday}-close`"
                                        v-model="selectedSchedule[`${holiday}CloseTime`]"
                                        size="sm"
                                        :locale="currentLocale"
                                        :hour12="true"
                                        hide-seconds
                                        hide-header
                                        reset-button
                                        :label-reset-button="$t('dmp.timeSchedules.resetButton')"
                                        :label-close-button="$t('dmp.timeSchedules.closeButton')"
                                        :placeholder="$t('dmp.timeSchedules.noTimeSelected')"
                                        class="flex-grow-1"
                                      />
                                      <b-button
                                        v-if="selectedSchedule[`${holiday}CloseTime`]"
                                        variant="flat-secondary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.copyTime')"
                                        @click="copyTime('close', selectedSchedule[`${holiday}CloseTime`])"
                                      >
                                        <feather-icon icon="CopyIcon" size="12" />
                                      </b-button>
                                      <b-button
                                        v-if="copiedTime.close && selectedSchedule[`${holiday}CloseTime`] !== copiedTime.close"
                                        variant="flat-primary"
                                        size="sm"
                                        class="btn-icon ml-25 time-action-btn"
                                        v-b-tooltip.hover
                                        :title="$t('dmp.timeSchedules.pasteTime')"
                                        @click="pasteTime('close', `${holiday}CloseTime`)"
                                      >
                                        <feather-icon icon="ClipboardIcon" size="12" />
                                      </b-button>
                                    </div>
                                  </b-form-group>
                                </div>

                                <!-- Sunrise/Sunset Options -->
                                <div class="mt-3 mb-2">
                                  <validation-provider
                                    :name="$t('dmp.timeSchedules.sunriseSunset')"
                                    :rules="
                                      sunriseDays.close.length > 0 ? 'required' : ''
                                    "
                                    v-slot="{ errors, valid }"
                                  >
                                    <b-form-group
                                      :label="$t('dmp.timeSchedules.sunriseSunset')"
                                      label-for="sunrise-sunset-close"
                                      label-cols="3"
                                      label-size="sm"
                                      :state="errors[0] ? false : valid ? true : null"
                                      :invalid-feedback="errors[0]"
                                    >
                                      <b-form-select
                                        id="sunrise-sunset-close"
                                        v-model="sunriseSelected.close"
                                        :options="sunriseOptionsList"
                                        size="sm"
                                        :state="errors[0] ? false : valid ? true : null"
                                      />
                                    </b-form-group>
                                  </validation-provider>
                                </div>

                                <div class="mb-2">
                                  <validation-provider
                                    :name="$t('dmp.timeSchedules.beforeAfter')"
                                    :rules="
                                      sunriseDays.close.length > 0 ? 'required' : ''
                                    "
                                    v-slot="{ errors, valid }"
                                  >
                                    <b-form-group
                                      :label="$t('dmp.timeSchedules.beforeAfter')"
                                      label-for="before-after-close"
                                      label-cols="3"
                                      label-size="sm"
                                      :state="errors[0] ? false : valid ? true : null"
                                      :invalid-feedback="errors[0]"
                                    >
                                      <b-form-select
                                        id="before-after-close"
                                        v-model="beforeAfterSelected.close"
                                        :options="beforeAfterOptionsList"
                                        size="sm"
                                        :state="errors[0] ? false : valid ? true : null"
                                      />
                                    </b-form-group>
                                  </validation-provider>
                                </div>

                                <!-- Days checkboxes -->
                                <div class="pl-3">
                                  <b-form-checkbox-group
                                    v-model="sunriseDays.close"
                                    :options="daysCheckboxOptions"
                                    size="sm"
                                    class="days-checkbox-group"
                                  />
                                </div>
                              </b-col>
                            </b-row>
                          </b-col>

                          <!-- Action Buttons -->
                          <b-col cols="12" class="d-flex justify-content-end mt-3">
                            <b-button
                              v-if="isEditingSchedule"
                              variant="outline-danger"
                              size="sm"
                              class="mr-1"
                              @click="deleteSchedule"
                              :disabled="isSavingSchedule || isDeletingSchedule"
                            >
                              <b-spinner v-if="isDeletingSchedule" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="TrashIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{ $t("actions.delete") }}</span>
                            </b-button>
                            <b-button
                              variant="outline-secondary"
                              size="sm"
                              class="mr-1"
                              @click="cancelEdit"
                              :disabled="isSavingSchedule || isDeletingSchedule"
                            >
                              <feather-icon icon="XIcon" size="14" class="mr-25" />
                              <span>{{ $t("actions.cancel") }}</span>
                            </b-button>
                            <b-button
                              variant="primary"
                              size="sm"
                              type="submit"
                              :disabled="isSavingSchedule || isDeletingSchedule"
                            >
                              <b-spinner v-if="isSavingSchedule" small class="mr-25" />
                              <feather-icon
                                v-else
                                icon="SaveIcon"
                                size="14"
                                class="mr-25"
                              />
                              <span>{{
                                isSavingSchedule
                                  ? $t("actions.saving")
                                  : $t("actions.save")
                              }}</span>
                            </b-button>
                          </b-col>
                        </b-row>
                      </b-form>
                    </validation-observer>
                  </b-card-body>
                </b-card>

                <!-- Empty State -->
                <b-card
                  v-else
                  no-body
                  class="d-flex align-items-center justify-content-center"
                  style="min-height: 400px"
                >
                  <div class="text-center p-3">
                    <feather-icon icon="ClockIcon" size="48" class="text-muted mb-2" />
                    <p class="text-muted mb-0">
                      {{ $t("dmp.timeSchedules.selectSchedule") }}
                    </p>
                  </div>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import Swal from "sweetalert2";
import { useTemplateMode } from "./useTemplateMode";
import flatPickr from "vue-flatpickr-component";
import { formatToTwoDigits } from "./usePanelConfig";

// Custom validation rule for DD/MM format
extend("date_format", {
  validate: (value) => {
    if (!value) return true; // Optional field
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])$/;
    return regex.test(value);
  },
  message: "El formato debe ser DD/MM (ej: 25/12)",
});

export default {
  name: "DmpPanelsConfigTimeSchedules",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
    flatPickr,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    timeSchedulesData: {
      type: Array,
      default: () => [],
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { isTemplateMode, emitTemplateUpdate, nextSequentialId } = useTemplateMode(props, emit);

    // Get current locale
    const currentLocale = computed(() => proxy.$i18n.locale);

    // Form validation
    const { refFormObserver, getValidationState } = formValidation();
    const scheduleFormObserver = ref(null);

    // State
    const selectedSchedule = ref(null);
    const isEditingSchedule = ref(false);
    const isSavingSchedule = ref(false);
    const isDeletingSchedule = ref(false);
    const isLoadingSchedules = ref(false);
    const temporaryScheduleEnabled = ref(false);

    const datePickerConfig = {
      dateFormat: "d/m",
      allowInput: true,
      locale: {
        firstDayOfWeek: 1,
      },
    };

    // Sunrise/Sunset selected values
    const sunriseSelected = ref({
      open: null,
      close: null,
    });

    const beforeAfterSelected = ref({
      open: null,
      close: null,
    });

    // Sunrise/Sunset options (computed for reactivity)
    const sunriseOptionsList = computed(() => [
      { value: null, text: t("dmp.timeSchedules.select") },
      { value: "sunrise", text: t("dmp.timeSchedules.sunrise") },
      { value: "sunset", text: t("dmp.timeSchedules.sunset") },
    ]);

    const beforeAfterOptionsList = computed(() => [
      { value: "At", text: "At" },
      { value: "5min. before", text: t("dmp.timeSchedules.beforeAfterOptions.5minbefore") },
      { value: "10min. before", text: t("dmp.timeSchedules.beforeAfterOptions.10minbefore") },
      { value: "15min. before", text: t("dmp.timeSchedules.beforeAfterOptions.15minbefore") },
      { value: "30min. before", text: t("dmp.timeSchedules.beforeAfterOptions.30minbefore") },
      { value: "45min. before", text: t("dmp.timeSchedules.beforeAfterOptions.45minbefore") },
      { value: "60min. before", text: t("dmp.timeSchedules.beforeAfterOptions.60minbefore") },
      { value: "5min. after", text: t("dmp.timeSchedules.beforeAfterOptions.5minafter") },
      { value: "10min. after", text: t("dmp.timeSchedules.beforeAfterOptions.10minafter") },
      { value: "15min. after", text: t("dmp.timeSchedules.beforeAfterOptions.15minafter") },
      { value: "30min. after", text: t("dmp.timeSchedules.beforeAfterOptions.30minafter") },
      { value: "45min. after", text: t("dmp.timeSchedules.beforeAfterOptions.45minafter") },
      { value: "60min. after", text: t("dmp.timeSchedules.beforeAfterOptions.60minafter") },
    ]);

    const sunriseDays = ref({
      open: [],
      close: [],
    });

    // Days and holidays
    const weekDays = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const holidays = ["holidayA", "holidayB", "holidayC"];

    // Mapping between weekday names and checkbox values
    const dayMapping = {
      sunday: "sun",
      monday: "mon",
      tuesday: "tue",
      wednesday: "wed",
      thursday: "thu",
      friday: "fri",
      saturday: "sat",
    };

    const daysCheckboxOptions = computed(() => [
      { text: t("dmp.timeSchedules.days.sun"), value: "sun" },
      { text: t("dmp.timeSchedules.days.mon"), value: "mon" },
      { text: t("dmp.timeSchedules.days.tue"), value: "tue" },
      { text: t("dmp.timeSchedules.days.wed"), value: "wed" },
      { text: t("dmp.timeSchedules.days.thu"), value: "thu" },
      { text: t("dmp.timeSchedules.days.fri"), value: "fri" },
      { text: t("dmp.timeSchedules.days.sat"), value: "sat" },
    ]);

    // Table fields
    const scheduleTableFields = computed(() => [
      {
        key: "number",
        label: t("dmp.timeSchedules.number"),
        sortable: true,
      },
      {
        key: "name",
        label: t("dmp.timeSchedules.name"),
        sortable: true,
      },
    ]);

    // Local schedules data
    const localSchedules = computed(() => {
      return props.timeSchedulesData || [];
    });

    const createEmptySchedule = (number) => ({
      scheduleNumber: number,
      scheduleName: "",
      sundayOpenTime: "",
      sundayCloseTime: "",
      mondayOpenTime: "",
      mondayCloseTime: "",
      tuesdayOpenTime: "",
      tuesdayCloseTime: "",
      wednesdayOpenTime: "",
      wednesdayCloseTime: "",
      thursdayOpenTime: "",
      thursdayCloseTime: "",
      fridayOpenTime: "",
      fridayCloseTime: "",
      saturdayOpenTime: "",
      saturdayCloseTime: "",
      holidayAOpenTime: "",
      holidayACloseTime: "",
      holidayBOpenTime: "",
      holidayBCloseTime: "",
      holidayCOpenTime: "",
      holidayCCloseTime: "",
      temporarySchedule: "N",
      beginDate: "",
      endDate: "",
    });

    // Constants & Regex
    const REGEX_BEFORE = /(\d+)\s*min\.?\s*before/i;
    const REGEX_AFTER = /(\d+)\s*min\.?\s*after/i;
    const REGEX_12H = /(\d{1,2}):(\d{2})\s*(a\.?\s*m\.?|p\.?\s*m\.?)/i;
    const EVENT_SUNRISE = "sunrise";
    const EVENT_SUNSET = "sunset";
    const MODIFIER_AT = "At";

    // Helper: Convert 24h (HH:mm:ss) to 12h (hh:mm p. m.)
    const formatTo12Hour = (timeStr) => {
      if (!timeStr) return "";
      if (timeStr.toLowerCase().includes("m.")) return timeStr;

      const [hours, minutes] = timeStr.split(":");
      if (hours === undefined || minutes === undefined) return timeStr;

      let h = parseInt(hours, 10);
      const m = minutes;
      const ampm = h >= 12 ? "p. m." : "a. m.";
      h = h % 12;
      h = h ? h : 12;
      return `${h.toString().padStart(2, "0")}:${m} ${ampm}`;
    };

    // Helper: Convert 12h (hh:mm p. m.) to 24h (HH:mm:ss)
    const formatTo24Hour = (timeStr) => {
      if (!timeStr) return "";
      const lower = timeStr.toLowerCase().trim();

      if (!lower.includes("m.")) return timeStr;

      const match = lower.match(REGEX_12H);
      if (!match) return timeStr;

      let [_, h, m, period] = match;
      h = parseInt(h, 10);
      const isPM = period.includes("p");

      if (isPM && h !== 12) h += 12;
      if (!isPM && h === 12) h = 0;

      return `${h.toString().padStart(2, "0")}:${m}:00`;
    };

    // Parse time value to determine if it's sunrise/sunset or regular time
    const parseTimeValue = (value) => {
      if (!value)
        return { time: "", isSunrise: false, event: null, modifier: null, days: [] };

      const lowerValue = value.toLowerCase();

      // Check if it's a sunrise/sunset event
      if (lowerValue.includes(EVENT_SUNRISE) || lowerValue.includes(EVENT_SUNSET)) {
        const event = lowerValue.includes(EVENT_SUNRISE) ? EVENT_SUNRISE : EVENT_SUNSET;

        let modifier = MODIFIER_AT;

        const beforeMatch = value.match(REGEX_BEFORE);
        if (beforeMatch) {
          modifier = `${beforeMatch[1]}min. before`;
        } else if (lowerValue.includes("before") && !beforeMatch) {
          modifier = MODIFIER_AT;
        }

        // Check for "after"
        const afterMatch = value.match(REGEX_AFTER);
        if (afterMatch) {
          modifier = `${afterMatch[1]}min. after`;
        } else if (lowerValue.includes("after") && !afterMatch) {
          modifier = MODIFIER_AT;
        }

        // Check for "At" explicitly
        if (lowerValue.includes("at") && !beforeMatch && !afterMatch) {
          modifier = MODIFIER_AT;
        }

        return { time: "", isSunrise: true, event, modifier, days: [] };
      }

      // Regular time
      const time24 = formatTo24Hour(value);
      return { time: time24, isSunrise: false, event: null, modifier: null, days: [] };
    };

    // Row selection
    const onRowSelected = (items) => {
      if (items.length > 0) {
        const selected = items[0];
        const scheduleData = {
          ...createEmptySchedule(selected.scheduleNumber),
          ...selected,
          scheduleNumber: parseInt(selected.scheduleNumber)
        };

        // Ensure name is uppercase
        if (scheduleData.scheduleName) {
          scheduleData.scheduleName = scheduleData.scheduleName.toUpperCase();
        }

        // Reset sunrise/sunset options
        sunriseSelected.value.open = null;
        sunriseSelected.value.close = null;
        beforeAfterSelected.value.open = null;
        beforeAfterSelected.value.close = null;
        sunriseDays.value.open = [];
        sunriseDays.value.close = [];

        // Flags to track if we've already set the global dropdowns for Open/Close
        // (UI assumes all sunrise days share the same config per column)
        const flags = { open: false, close: false };

        const processColumn = (day, colType) => {
          const key = `${day}${colType}Time`;
          const rawValue = selected[key];
          const parsed = parseTimeValue(rawValue);
          const typeLower = colType.toLowerCase(); // 'open' or 'close'

          if (parsed.isSunrise) {
            scheduleData[key] = ""; // Clear time input
            sunriseDays.value[typeLower].push(dayMapping[day]);

            // Set global options from the first sunrise value encountered
            if (!flags[typeLower]) {
              sunriseSelected.value[typeLower] = parsed.event;
              beforeAfterSelected.value[typeLower] = parsed.modifier;
              flags[typeLower] = true;
            }
          } else {
            scheduleData[key] = parsed.time;
          }
        };

        weekDays.forEach((day) => {
          processColumn(day, "Open");
          processColumn(day, "Close");
        });

        // Parse holidays to ensure 24h format for timepicker
        holidays.forEach((holiday) => {
          scheduleData[`${holiday}OpenTime`] = formatTo24Hour(
            selected[`${holiday}OpenTime`]
          );
          scheduleData[`${holiday}CloseTime`] = formatTo24Hour(
            selected[`${holiday}CloseTime`]
          );
        });

        // Format dates: "0000" -> "", "1902" -> "19/02"
        if (
          scheduleData.beginDate &&
          scheduleData.beginDate !== "0000" &&
          scheduleData.beginDate.length === 4
        ) {
          scheduleData.beginDate = `${scheduleData.beginDate.substring(
            0,
            2
          )}/${scheduleData.beginDate.substring(2, 4)}`;
        } else if (scheduleData.beginDate === "0000") {
          scheduleData.beginDate = "";
        }

        if (
          scheduleData.endDate &&
          scheduleData.endDate !== "0000" &&
          scheduleData.endDate.length === 4
        ) {
          scheduleData.endDate = `${scheduleData.endDate.substring(
            0,
            2
          )}/${scheduleData.endDate.substring(2, 4)}`;
        } else if (scheduleData.endDate === "0000") {
          scheduleData.endDate = "";
        }

        selectedSchedule.value = scheduleData;
        temporaryScheduleEnabled.value = scheduleData.temporarySchedule === "Y";
        isEditingSchedule.value = !!selected.scheduleName;
      } else {
        selectedSchedule.value = null;
        isEditingSchedule.value = false;
        sunriseSelected.value.open = null;
        sunriseSelected.value.close = null;
        beforeAfterSelected.value.open = null;
        beforeAfterSelected.value.close = null;
        sunriseDays.value.open = [];
        sunriseDays.value.close = [];
      }
    };

    // Add new schedule
    const addNewSchedule = () => {
      let nextNumber = 1;
      for (let i = 1; i <= 99; i++) {
        const num = String(i).padStart(2, "");
        const exists = props.timeSchedulesData.find((s) => s.scheduleNumber === num);
        if (!exists) {
          nextNumber = i;
          break;
        }
      }

      selectedSchedule.value = createEmptySchedule(String(nextNumber).padStart(2, ""));
      temporaryScheduleEnabled.value = false;
      isEditingSchedule.value = false;
      sunriseSelected.value.open = null;
      sunriseSelected.value.close = null;
      beforeAfterSelected.value.open = null;
      beforeAfterSelected.value.close = null;
    };

    // Format schedule number
    const formatScheduleNumber = () => {
      if (selectedSchedule.value?.scheduleNumber) {
        selectedSchedule.value.scheduleNumber = formatToTwoDigits(
          selectedSchedule.value.scheduleNumber
        );
      }
    };

    const buildTimeValue = (inputValue, sunriseEvent, beforeAfter, day, selectedDays) => {
      // Only use sunrise/sunset if:
      // 1. Sunrise/sunset is selected
      // 2. This specific day is checked in selectedDays
      if (sunriseEvent && beforeAfter && day && selectedDays) {
        const dayCheckboxValue = dayMapping[day];
        if (selectedDays.includes(dayCheckboxValue)) {
          // Capitalize event for display/save (sunrise -> Sunrise)
          const eventCapitalized =
            sunriseEvent.charAt(0).toUpperCase() + sunriseEvent.slice(1);

          if (beforeAfter === "At") {
            return `At ${eventCapitalized}`;
          }

          // Reconstruct human readable string from value "30min. before" -> "30 Min. Before"
          const modifierParts = beforeAfter.match(/(\d+)min\.\s*(before|after)/);
          if (modifierParts) {
            const minutes = modifierParts[1];
            const type = modifierParts[2] === "before" ? "Before" : "After";
            return `${minutes} Min. ${type} ${eventCapitalized}`;
          }

          return `${beforeAfter} ${eventCapitalized}`;
        }
      }

      // Otherwise return the regular input value formatted to 12h
      return formatTo12Hour(inputValue) || "";
    };

    // Helper to cleanup date for saving
    const cleanupDateForSave = (dateStr) => {
      if (!dateStr) return "";
      return dateStr.replace(/\//g, ""); // "19/02" -> "1902"
    };

    // Cancel edit
    const cancelEdit = () => {
      selectedSchedule.value = null;
      isEditingSchedule.value = false;
      temporaryScheduleEnabled.value = false;
      sunriseSelected.value.open = null;
      sunriseSelected.value.close = null;
      beforeAfterSelected.value.open = null;
      beforeAfterSelected.value.close = null;
      sunriseDays.value.open = [];
      sunriseDays.value.close = [];
    };

    // Save schedule
    const saveSchedule = async () => {
      const isValid = await scheduleFormObserver.value.validate();
      if (!isValid) return;

      isSavingSchedule.value = true;

      try {
        // Build time values: use sunrise/sunset if selectors are set, otherwise use input values
        const payload = {
          scheduleNumber: selectedSchedule.value.scheduleNumber,
          scheduleName: selectedSchedule.value.scheduleName?.toUpperCase() || "",
          sundayOpenTime: buildTimeValue(
            selectedSchedule.value.sundayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "sunday",
            sunriseDays.value.open
          ),
          sundayCloseTime: buildTimeValue(
            selectedSchedule.value.sundayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "sunday",
            sunriseDays.value.close
          ),
          mondayOpenTime: buildTimeValue(
            selectedSchedule.value.mondayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "monday",
            sunriseDays.value.open
          ),
          mondayCloseTime: buildTimeValue(
            selectedSchedule.value.mondayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "monday",
            sunriseDays.value.close
          ),
          tuesdayOpenTime: buildTimeValue(
            selectedSchedule.value.tuesdayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "tuesday",
            sunriseDays.value.open
          ),
          tuesdayCloseTime: buildTimeValue(
            selectedSchedule.value.tuesdayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "tuesday",
            sunriseDays.value.close
          ),
          wednesdayOpenTime: buildTimeValue(
            selectedSchedule.value.wednesdayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "wednesday",
            sunriseDays.value.open
          ),
          wednesdayCloseTime: buildTimeValue(
            selectedSchedule.value.wednesdayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "wednesday",
            sunriseDays.value.close
          ),
          thursdayOpenTime: buildTimeValue(
            selectedSchedule.value.thursdayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "thursday",
            sunriseDays.value.open
          ),
          thursdayCloseTime: buildTimeValue(
            selectedSchedule.value.thursdayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "thursday",
            sunriseDays.value.close
          ),
          fridayOpenTime: buildTimeValue(
            selectedSchedule.value.fridayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "friday",
            sunriseDays.value.open
          ),
          fridayCloseTime: buildTimeValue(
            selectedSchedule.value.fridayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "friday",
            sunriseDays.value.close
          ),
          saturdayOpenTime: buildTimeValue(
            selectedSchedule.value.saturdayOpenTime,
            sunriseSelected.value.open,
            beforeAfterSelected.value.open,
            "saturday",
            sunriseDays.value.open
          ),
          saturdayCloseTime: buildTimeValue(
            selectedSchedule.value.saturdayCloseTime,
            sunriseSelected.value.close,
            beforeAfterSelected.value.close,
            "saturday",
            sunriseDays.value.close
          ),
          // Holidays only support regular time format (no sunrise/sunset)
          holidayAOpenTime: formatTo12Hour(selectedSchedule.value.holidayAOpenTime) || "",
          holidayACloseTime:
            formatTo12Hour(selectedSchedule.value.holidayACloseTime) || "",
          holidayBOpenTime: formatTo12Hour(selectedSchedule.value.holidayBOpenTime) || "",
          holidayBCloseTime:
            formatTo12Hour(selectedSchedule.value.holidayBCloseTime) || "",
          holidayCOpenTime: formatTo12Hour(selectedSchedule.value.holidayCOpenTime) || "",
          holidayCCloseTime:
            formatTo12Hour(selectedSchedule.value.holidayCCloseTime) || "",
          temporarySchedule: temporaryScheduleEnabled.value ? "Y" : "N",
          beginDate: cleanupDateForSave(selectedSchedule.value.beginDate),
          endDate: cleanupDateForSave(selectedSchedule.value.endDate),
        };

        if (isTemplateMode.value) {
          const list = [...(props.timeSchedulesData || [])];
          const idx = list.findIndex(
            (s) =>
              parseInt(s.scheduleNumber, 10) ===
              parseInt(payload.scheduleNumber, 10)
          );
          const item = { ...(idx !== -1 ? list[idx] : {}), ...payload };
          if (idx !== -1) {
            item.id = list[idx].id != null ? list[idx].id : nextSequentialId(list);
            list.splice(idx, 1, item);
          } else {
            item.id = nextSequentialId(list);
            list.push(item);
          }

          emitTemplateUpdate("time_schedules", list);
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.common.success"),
              text: proxy.$t("dmp.timeSchedules.messages.scheduleSaveSuccess"),
              icon: "CheckIcon",
              variant: "success",
            },
          });
          selectedSchedule.value = null;
          isEditingSchedule.value = false;
          temporaryScheduleEnabled.value = false;
          isSavingSchedule.value = false;
          return;
        }

        const response = await store.dispatch("dmp_panels/setTimeSchedule", {
          uuid: props.uuid,
          timeData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.timeSchedules.messages.scheduleSaveSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Emit event to notify operation completed
        emit("operation-completed");

        // Clear selection
        selectedSchedule.value = null;
        isEditingSchedule.value = false;
        temporaryScheduleEnabled.value = false;
      } catch (error) {
        console.error("Error saving time schedule:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.timeSchedules.messages.scheduleSaveError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSavingSchedule.value = false;
      }
    };

    // Delete schedule
    const deleteSchedule = async () => {
      const result = await Swal.fire({
        title: proxy.$t("dmp.timeSchedules.confirmDelete"),
        text: proxy.$t("dmp.timeSchedules.confirmDeleteMessage"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: proxy.$t("actions.delete"),
        cancelButtonText: proxy.$t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-secondary ml-1",
        },
        buttonsStyling: false,
      });

      if (!result.isConfirmed) return;

      if (isTemplateMode.value) {
        const list = (props.timeSchedulesData || []).filter(
          (s) =>
            parseInt(s.scheduleNumber, 10) !==
            parseInt(selectedSchedule.value.scheduleNumber, 10)
        );
        emitTemplateUpdate("time_schedules", list);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.timeSchedules.messages.scheduleDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        selectedSchedule.value = null;
        isEditingSchedule.value = false;
        temporaryScheduleEnabled.value = false;
        return;
      }

      isDeletingSchedule.value = true;

      try {
        await store.dispatch("dmp_panels/deleteTimeSchedule", {
          uuid: props.uuid,
          scheduleNumber: selectedSchedule.value.scheduleNumber,
        });

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.timeSchedules.messages.scheduleDeleteSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        // Emit event to notify operation completed
        emit("operation-completed");

        // Clear selection
        selectedSchedule.value = null;
        isEditingSchedule.value = false;
        temporaryScheduleEnabled.value = false;
      } catch (error) {
        console.error("Error deleting time schedule:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.error"),
            text: proxy.$t("dmp.timeSchedules.messages.scheduleDeleteError"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isDeletingSchedule.value = false;
      }
    };

    // Format date input to DD/MM
    const formatDateInput = (value, field) => {
      // Remove non-numeric characters except /
      let cleaned = value.replace(/[^0-9]/g, "");

      // Auto-add slash after 2 digits
      if (cleaned.length >= 2) {
        cleaned = cleaned.substring(0, 2) + "/" + cleaned.substring(2, 4);
      }

      // Update the model
      selectedSchedule.value[field] = cleaned;
    };

    // Helper to check if a day is selected for sunrise/sunset
    const isDaySelectedForSunrise = (day, type) => {
      const shortDay = dayMapping[day];
      return sunriseDays.value[type].includes(shortDay);
    };

    // Helper to get the constructed sunrise/sunset string
    const getSunriseString = (type) => {
      const event = sunriseSelected.value[type];
      const modifier = beforeAfterSelected.value[type];

      if (!event || !modifier) return "";

      if (modifier === "At") {
        return `At ${event}`;
      }
      return `${modifier} ${event}`;
    };

    // Copy/Paste time functionality
    const copiedTime = ref({ open: null, close: null });

    const copyTime = (column, timeValue) => {
      copiedTime.value[column] = timeValue;
    };

    const pasteTime = (column, fieldKey) => {
      if (copiedTime.value[column] && selectedSchedule.value) {
        proxy.$set(selectedSchedule.value, fieldKey, copiedTime.value[column]);
      }
    };

    const applyTimeToAll = (column) => {
      if (!copiedTime.value[column] || !selectedSchedule.value) return;
      const suffix = column === 'open' ? 'OpenTime' : 'CloseTime';
      const time = copiedTime.value[column];

      weekDays.forEach((day) => {
        if (!isDaySelectedForSunrise(day, column)) {
          proxy.$set(selectedSchedule.value, `${day}${suffix}`, time);
        }
      });
      holidays.forEach((holiday) => {
        proxy.$set(selectedSchedule.value, `${holiday}${suffix}`, time);
      });
    };

    // Row class for highlighting
    const rowClass = (item, type) => {
      if (!item || type !== "row") return "";
      if (selectedSchedule.value?.scheduleNumber === item.scheduleNumber) {
        return "table-active";
      }
      return "";
    };

    watch(
      () => props.timeSchedulesData,
      (newData) => {
        if (newData) {
          // Data is already computed in localSchedules
        }
      },
      { deep: true }
    );

    return {
      // Form
      refFormObserver,
      scheduleFormObserver,
      getValidationState,

      // Locale
      currentLocale,

      // State
      selectedSchedule,
      isEditingSchedule,
      isSavingSchedule,
      isDeletingSchedule,
      isLoadingSchedules,
      temporaryScheduleEnabled,

      // Options
      sunriseSelected,
      sunriseOptionsList,
      beforeAfterSelected,
      beforeAfterOptionsList,
      sunriseDays,
      daysCheckboxOptions,
      weekDays,
      holidays,

      // Table
      scheduleTableFields,
      localSchedules,

      // Methods
      onRowSelected,
      addNewSchedule,
      formatScheduleNumber,
      formatDateInput,
      datePickerConfig,
      cancelEdit,
      saveSchedule,
      deleteSchedule,
      rowClass,
      isDaySelectedForSunrise,
      getSunriseString,

      // Copy/Paste time
      copiedTime,
      copyTime,
      pasteTime,
      applyTimeToAll,
    };
  },
};
</script>

<style lang="scss" scoped>
.table-column {
  max-height: 600px;
  overflow-y: auto;
}

.details-column {
  overflow-y: auto;
}

.card-header-custom {
  background-color: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
  padding: 1rem;
}

.dark-layout {
  .card-header-custom {
    background-color: #283046;
    border-bottom-color: #404656;
  }
}

.days-checkbox-group {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.time-action-btn {
  padding: 0.25rem;
  min-width: unset;
  flex-shrink: 0;
}

::v-deep {
  .table-active {
    background-color: rgba(115, 103, 240, 0.12) !important;
  }

  .dark-layout .table-active {
    background-color: rgba(115, 103, 240, 0.2) !important;
  }
}
</style>
